import { InputChangeEventDetail, TextareaInputChangeEventDetail, uuidv4 } from '@wppopen/components-library'
import {
  WppIconRemoveCircle,
  WppTextareaInput,
  WppIconSortDecrease,
  WppTypography,
  WppActionButton,
  WppIconPlusCircle,
} from '@wppopen/components-library-react'
import { WppInputCustomEvent, WppTextareaInputCustomEvent } from '@wppopen/components-library/dist/types/components'
import {
  getKeywordsFromReducer,
  parseStringToJSX,
} from 'components/KeywordsElements/Highlighting/HighlightedTextBodySpan/utils'
import ViewTaskPageContext from 'context/viewTaskPageContext/viewTaskPageContext'
import { MouseEvent, Dispatch, SetStateAction, useRef, useContext, useMemo } from 'react'

interface TextContainerListProps {
  textContent: string[]
  setTextContent: Dispatch<SetStateAction<string[]>>
  isEditModeEnabled: boolean
}

function TextContainerList({ textContent, setTextContent, isEditModeEnabled }: TextContainerListProps) {
  const keysRef = useRef<string[] | null>(textContent.map(() => uuidv4()))
  const resetKeyRef = (newTextList: string[]) => (keysRef.current = newTextList.map(() => uuidv4()))

  const { shouldShowKeywords, keywordsAndPhrasesReducerstate } = useContext(ViewTaskPageContext)

  const reducerKeywordsList = useMemo(() => {
    return getKeywordsFromReducer(keywordsAndPhrasesReducerstate)
  }, [keywordsAndPhrasesReducerstate])

  const handleInputChange = (
    event: WppInputCustomEvent<InputChangeEventDetail> | WppTextareaInputCustomEvent<TextareaInputChangeEventDetail>,
    idx?: number,
  ) => {
    event.preventDefault()
    const { target } = event

    const newTextList = Array.from(textContent)
    newTextList[idx!] = target.value
    setTextContent(newTextList)
  }

  const handleDeleteClick = (_: MouseEvent<HTMLWppIconRemoveCircleElement>, idx: number) => {
    const newTextList = Array.from(textContent)
    newTextList.splice(idx, 1)
    setTextContent(newTextList)
    resetKeyRef(newTextList)
  }

  const handleShiftDownClick = (_: MouseEvent<HTMLWppIconSortByDecreaseElement>, idx: number) => {
    const newTextList = Array.from(textContent)
    const splicedEntry = newTextList.splice(idx, 1)
    newTextList.splice(idx + 1, 0, splicedEntry[0])
    setTextContent(newTextList)
    resetKeyRef(newTextList)
  }

  const handleAddClick = () => {
    let newTextList = Array.from(textContent)
    newTextList = [...newTextList, '']
    setTextContent(newTextList)
    resetKeyRef(newTextList)
  }

  return (
    <>
      <ul data-testid="product-bullet-points">
        {textContent.map((item, idx) => (
          <li key={keysRef.current![idx]}>
            {isEditModeEnabled ? (
              <>
                <WppIconRemoveCircle onClick={e => handleDeleteClick(e, idx)} data-testid="edit-list-item--remove" />
                <WppTextareaInput
                  value={textContent[idx]}
                  onWppChange={e => handleInputChange(e, idx)}
                  data-testid="edit-list-item--textarea"
                  rows={1}
                  role="textbox"
                />
                {idx !== textContent.length - 1 && (
                  <WppIconSortDecrease
                    onClick={e => handleShiftDownClick(e, idx)}
                    data-testid="edit-list-item--shift"
                  />
                )}
              </>
            ) : (
              <WppTypography type="s-body" tag="p">
                {shouldShowKeywords
                  ? parseStringToJSX(item, reducerKeywordsList, keywordsAndPhrasesReducerstate.activeKeyword)
                  : item}
              </WppTypography>
            )}
          </li>
        ))}
      </ul>
      {isEditModeEnabled && (
        <WppActionButton onClick={handleAddClick} data-testid="edit-list-item--add">
          Add Item
          <WppIconPlusCircle slot="icon-start" />
        </WppActionButton>
      )}
    </>
  )
}

export default TextContainerList
